import { useWindowDimensions } from 'react-native';

import webStyles from '../style/webStyles';
import mobileStyles from '../style/mobileStyles';
import {
  isWeb as checkIsWeb,
  isIOS as checkIsIOS,
  isAndroid as checkIsAndroid,
} from '../util/platform';

/**
 * Custom hook for responsive styling across web and mobile platforms.
 *
 * Centralizes platform detection and breakpoint logic that was previously
 * scattered across 50+ locations in the codebase.
 *
 * @returns {Object} Responsive style utilities and flags
 *
 * @example
 * const { containerStyle, isDesktop, isMobile } = useResponsiveStyles();
 *
 * return (
 *   <View style={containerStyle}>
 *     {isDesktop ? <DesktopLayout /> : <MobileLayout />}
 *   </View>
 * );
 */
const useResponsiveStyles = () => {
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();

  const isWeb = checkIsWeb();
  const isIOS = checkIsIOS();
  const isAndroid = checkIsAndroid();

  // Primary breakpoint used throughout the app (800px)
  const isDesktop = isWeb && screenWidth >= 800;

  // Additional breakpoints for specific use cases
  // Note: Some screens use different breakpoints intentionally (1000, 1500)
  // Use these specific flags when needed rather than changing the defaults
  const isLargeDesktop = isWeb && screenWidth >= 1000;
  const isExtraLargeDesktop = isWeb && screenWidth >= 1500;

  const isMobile = !isDesktop;
  const isNativeMobile = isIOS || isAndroid;

  // Common style selections
  const containerStyle = isDesktop ? webStyles.container : mobileStyles.container;
  const commentsContainerStyle = isDesktop
    ? webStyles.commentsContainer
    : mobileStyles.commentsContainer;
  const commentContainerStyle = isDesktop
    ? webStyles.commentContainer
    : mobileStyles.commentContainer;

  // Style getter for any web/mobile style pair
  const getStyle = (webStyle, mobileStyle) => (isDesktop ? webStyle : mobileStyle);

  // Width helper commonly used for form inputs
  const getResponsiveWidth = (desktopWidth, mobileWidth) =>
    isDesktop ? desktopWidth : mobileWidth;

  return {
    // Dimensions
    screenWidth,
    screenHeight,

    // Platform flags
    isWeb,
    isIOS,
    isAndroid,
    isNativeMobile,

    // Breakpoint flags
    isDesktop,
    isMobile,
    isLargeDesktop,
    isExtraLargeDesktop,

    // Pre-computed common styles
    containerStyle,
    commentsContainerStyle,
    commentContainerStyle,

    // Utility functions
    getStyle,
    getResponsiveWidth,

    // Raw style objects for custom combinations
    webStyles,
    mobileStyles,
  };
};

export default useResponsiveStyles;
